/**
 * Context menu for file tree operations
 */

import type { ContextMenuItem, ContextMenuState } from './types.js';

/**
 * Get context menu items based on target type
 */
export function getContextMenuItems(targetType: 'file' | 'folder' | 'empty'): ContextMenuItem[] {
  const items: ContextMenuItem[] = [];

  if (targetType === 'folder' || targetType === 'empty') {
    items.push({
      id: 'new-file',
      label: 'New File',
      icon: 'file-plus'
    });
    items.push({
      id: 'new-folder',
      label: 'New Folder',
      icon: 'folder-plus'
    });
  }

  if (targetType === 'folder') {
    items.push({ id: 'separator-1', label: '', separator: true });
    items.push({ id: 'rename', label: 'Rename', icon: 'edit' });
    items.push({ id: 'delete', label: 'Delete', icon: 'trash' });
  }

  if (targetType === 'file') {
    items.push({ id: 'rename', label: 'Rename', icon: 'edit' });
    items.push({ id: 'duplicate', label: 'Duplicate', icon: 'copy' });
    items.push({ id: 'delete', label: 'Delete', icon: 'trash' });
  }

  return items;
}

/**
 * SVG icons for context menu items
 */
const icons: Record<string, string> = {
  'file-plus': `<svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M440-240h80v-120h120v-80H520v-120h-80v120H320v80h120v120ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Z"/></svg>`,
  'folder-plus': `<svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M560-320h80v-80h80v-80h-80v-80h-80v80h-80v80h80v80ZM160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h240l80 80h320q33 0 56.5 23.5T880-640v400q0 33-23.5 56.5T800-160H160Z"/></svg>`,
  'edit': `<svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Z"/></svg>`,
  'copy': `<svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M360-240q-33 0-56.5-23.5T280-320v-480q0-33 23.5-56.5T360-880h360q33 0 56.5 23.5T800-800v480q0 33-23.5 56.5T720-240H360Zm-120 120q-33 0-56.5-23.5T160-200v-520h80v520h400v80H240Z"/></svg>`,
  'trash': `<svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Z"/></svg>`,
};

/**
 * Render the context menu element
 */
export function renderContextMenu(
  state: ContextMenuState,
  onAction: (action: string) => void
): HTMLElement {
  const menu = document.createElement('div');
  menu.className = 'context-menu';
  menu.style.left = `${state.x}px`;
  menu.style.top = `${state.y}px`;

  const items = getContextMenuItems(state.targetType);

  for (const item of items) {
    if (item.separator) {
      const sep = document.createElement('div');
      sep.className = 'context-menu-separator';
      menu.appendChild(sep);
    } else {
      const btn = document.createElement('button');
      btn.className = 'context-menu-item';
      btn.dataset.action = item.id;
      if (item.disabled) btn.disabled = true;

      const iconHtml = item.icon ? icons[item.icon] || '' : '';
      btn.innerHTML = `
        <span class="context-menu-icon">${iconHtml}</span>
        <span class="context-menu-label">${item.label}</span>
      `;

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        onAction(item.id);
      });

      menu.appendChild(btn);
    }
  }

  return menu;
}

/**
 * Show context menu at position, adjusting if off-screen
 */
export function showContextMenu(
  x: number,
  y: number,
  targetPath: string | null,
  targetType: 'file' | 'folder' | 'empty',
  onAction: (action: string) => void,
  onClose: () => void
): void {
  // Remove existing menu
  hideContextMenu();

  const state: ContextMenuState = {
    visible: true,
    x,
    y,
    targetPath,
    targetType,
  };

  const menu = renderContextMenu(state, (action) => {
    hideContextMenu();
    onAction(action);
  });

  document.body.appendChild(menu);

  // Adjust position if menu would go off screen
  const rect = menu.getBoundingClientRect();
  if (rect.right > window.innerWidth) {
    menu.style.left = `${window.innerWidth - rect.width - 8}px`;
  }
  if (rect.bottom > window.innerHeight) {
    menu.style.top = `${window.innerHeight - rect.height - 8}px`;
  }

  // Close when a press starts outside (mousedown, not click, so selection
  // drags released outside the menu don't dismiss it)
  const closeHandler = (e: MouseEvent) => {
    if (!menu.contains(e.target as Node)) {
      hideContextMenu();
      onClose();
    }
  };

  // Close on Escape
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      hideContextMenu();
      onClose();
    }
  };

  // Use setTimeout to avoid immediate close from the same click
  setTimeout(() => {
    document.addEventListener('mousedown', closeHandler);
    document.addEventListener('contextmenu', closeHandler);
    document.addEventListener('keydown', escHandler);
  }, 0);

  // Store handlers for cleanup
  (menu as HTMLElement & { _closeHandler?: typeof closeHandler; _escHandler?: typeof escHandler })._closeHandler = closeHandler;
  (menu as HTMLElement & { _closeHandler?: typeof closeHandler; _escHandler?: typeof escHandler })._escHandler = escHandler;
}

/**
 * Hide and remove the context menu
 */
export function hideContextMenu(): void {
  const menu = document.querySelector('.context-menu') as HTMLElement & {
    _closeHandler?: (e: MouseEvent) => void;
    _escHandler?: (e: KeyboardEvent) => void;
  } | null;

  if (menu) {
    // Remove event listeners
    if (menu._closeHandler) {
      document.removeEventListener('mousedown', menu._closeHandler);
      document.removeEventListener('contextmenu', menu._closeHandler);
    }
    if (menu._escHandler) {
      document.removeEventListener('keydown', menu._escHandler);
    }
    menu.remove();
  }
}
